import { useMemo, useState } from 'react';
import { useVault } from '../lib/vault';
import { CYCLE_LABEL, annualMoney, formatMoney, periodMoney } from '@shared/money';
import type { ItemForUser, ItemKind } from '@shared/types';
import { KIND_LABEL, PAYMENT_LABEL, PORTAL_LABEL } from '@shared/types';
import { ItemIcon } from '../components/ItemIcon';
import { ItemDetail } from '../components/ItemDetail';
import { ItemEditor } from '../components/ItemEditor';
import { plural } from '@shared/text';

type Filter = 'all' | 'mine' | 'rotation' | 'cancelled' | 'free';
type SortKey = 'title' | 'cost' | 'updated';

/** Metadaten als CSV - bewusst ohne Passwoerter, damit die Datei harmlos bleibt. */
function exportCsv(items: ItemForUser[], nameOf: (uid: string | null) => string): void {
  const head = [
    'Dienst',
    'Art',
    'Kategorie',
    'Status',
    'Kostet',
    // Der eingetragene Betrag in seiner Währung - so steht er auf der Rechnung.
    'Betrag',
    'Währung',
    'netto oder brutto',
    'Steuersatz %',
    'Abrechnung',
    // Umgerechnet und aufgeschlüsselt, je Abrechnung und aufs Jahr.
    'Netto je Abrechnung (EUR)',
    'Umsatzsteuer je Abrechnung (EUR)',
    'Brutto je Abrechnung (EUR)',
    'Brutto aufs Jahr (EUR)',
    'Preis je Platz',
    'Plätze bezahlt',
    'Plätze vergeben',
    'Abbuchung am',
    'Zahlungsmittel',
    'Zahlungsdetail',
    'Rechnungsweg',
    'Rechnungslink',
    'Verlängerung',
    'Kündigen bis',
    'Gekauft am',
    'Verantwortlich',
    'Verwalter',
  ];

  /** Deutsches Zahlenformat ohne Währungszeichen - so liest es jede Tabelle. */
  const zahl = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');

  const rows = items.map((i) => {
    const jeAbrechnung = periodMoney(i);
    return [
      i.title,
      KIND_LABEL[i.kind],
      i.category,
      i.status,
      i.hasCost ? 'ja' : 'nein',
      i.hasCost ? zahl(i.priceCents) : '',
      i.hasCost ? i.currency : '',
      i.hasCost ? (i.priceIsNet ? 'netto' : 'brutto') : '',
      i.hasCost ? String(i.vatRatePercent) : '',
      i.hasCost ? CYCLE_LABEL[i.cycle] : '',
      zahl(jeAbrechnung.netCents),
      zahl(jeAbrechnung.vatCents),
      zahl(jeAbrechnung.grossCents),
      zahl(annualMoney(i).grossCents),
      i.priceIsPerSeat ? 'ja' : 'nein',
      i.seats ?? '',
      i.assignments.filter((a) => a.openToAll || (a.assigneeUids ?? []).length > 0).length || '',
      i.billingDay ?? '',
      i.hasCost ? PAYMENT_LABEL[i.paymentMethod] : '',
      i.paymentDetail,
      PORTAL_LABEL[i.invoicePortal],
      i.invoiceUrl,
      i.renewalDate ?? '',
      i.cancelBeforeDate ?? '',
      i.purchaseDate ?? '',
      nameOf(i.ownerUid),
      String(i.accessUids.length),
    ];
  });

  const escape = (value: unknown): string => `"${String(value).replace(/"/g, '""')}"`;
  const csv = [head, ...rows].map((row) => row.map(escape).join(';')).join('\r\n');

  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `abo-uebersicht-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function VaultPage({
  focusIds,
  onClearFocus,
}: {
  /** Vom Hinweis mitgegebene Auswahl. null = alles zeigen. */
  focusIds?: string[] | null;
  onClearFocus?: () => void;
} = {}): JSX.Element {
  const { items, users, can } = useVault();
  const zeigtKosten = can('viewCosts');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [kindFilter, setKindFilter] = useState<'' | ItemKind>('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<SortKey>('title');
  const [detail, setDetail] = useState<ItemForUser | null>(null);
  const [editing, setEditing] = useState<{ item: ItemForUser | null } | null>(null);

  const nameOf = (uid: string | null): string => users.find((u) => u.uid === uid)?.name ?? '';

  const categories = useMemo(
    () => [...new Set(items.map((i) => i.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de')),
    [items],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Kommt die Auswahl von einem Hinweis, zaehlt nur sie - alle anderen
    // Filter wuerden das Ergebnis nur wieder verwaessern.
    if (focusIds && focusIds.length > 0) {
      const wanted = new Set(focusIds);
      return items
        .filter((i) => wanted.has(i.id))
        .sort((a, b) => a.title.localeCompare(b.title, 'de'));
    }

    let list = items.filter((item) => {
      if (filter === 'mine' && !item.wrappedKey) return false;
      if (filter === 'rotation' && !item.needsRotation) return false;
      if (filter === 'cancelled' && item.status !== 'cancelled') return false;
      if (filter !== 'cancelled' && item.status === 'cancelled') return false;
      if (filter === 'free' && item.hasCost) return false;
      if (kindFilter && item.kind !== kindFilter) return false;
      if (category && item.category !== category) return false;
      if (!q) return true;
      return [item.title, item.category, item.url, item.username, ...item.tags]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });

    list = [...list].sort((a, b) => {
      if (sort === 'cost') return annualMoney(b).grossCents - annualMoney(a).grossCents;
      if (sort === 'updated') return b.updatedAt.localeCompare(a.updatedAt);
      return a.title.localeCompare(b.title, 'de');
    });
    return list;
  }, [items, query, filter, kindFilter, category, sort, focusIds]);

  const visibleAnnual = visible.reduce((sum, i) => sum + annualMoney(i).grossCents, 0);

  // Der Detail-Dialog soll nach dem Speichern die frischen Daten zeigen.
  const liveDetail = detail ? (items.find((i) => i.id === detail.id) ?? null) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Zugänge</h1>
        </div>
        <div className="row-tight">
          {zeigtKosten ? (
            <button onClick={() => exportCsv(visible, nameOf)} disabled={visible.length === 0}>
              CSV
            </button>
          ) : null}
          <button className="btn-primary" onClick={() => setEditing({ item: null })}>
            Neuer Eintrag
          </button>
        </div>
      </div>

      {focusIds && focusIds.length > 0 ? (
        <div className="note note-warn" style={{ marginBottom: 14 }}>
          <div className="row">
            <span>
              Gefiltert auf {plural(visible.length, 'Eintrag', 'Einträge')} aus einem Hinweis.
            </span>
            <span className="spacer" />
            <button className="btn-sm" onClick={onClearFocus}>
              Filter aufheben
            </button>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="row filter-row" style={{ marginBottom: 14 }}>
          <input
            className="filter-search"
            style={{ maxWidth: 260 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suchen …"
            aria-label="Suchen"
          />
          <select style={{ maxWidth: 190 }} value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="all">Alle aktiven</option>
            <option value="mine">Nur meine Zugänge</option>
            <option value="rotation">Etwas zu tun</option>
            <option value="free">Ohne Kosten</option>
            <option value="cancelled">Gekündigte</option>
          </select>
          <select
            style={{ maxWidth: 180 }}
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as '' | ItemKind)}
          >
            <option value="">Alle Arten</option>
            <option value="shared">Geteilte Zugänge</option>
            <option value="seats">Persönliche Plätze</option>
            <option value="license">Gekaufte Lizenzen</option>
          </select>
          <select style={{ maxWidth: 190 }} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Alle Kategorien</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select style={{ maxWidth: 170 }} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="title">Name A–Z</option>
            <option value="cost">Teuerste zuerst</option>
            <option value="updated">Zuletzt geändert</option>
          </select>
          <span className="spacer" />
          <span className="small muted">
            {plural(visible.length, 'Eintrag', 'Einträge')}
            
          </span>
        </div>

        {visible.length === 0 ? (
          <div className="empty">
            {items.length === 0
              ? 'Noch nichts erfasst. Leg den ersten Zugang an.'
              : 'Kein Treffer für diese Filter.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Dienst</th>
                <th className="col-optional">Zugriff</th>
                {zeigtKosten ? <th className="col-optional">Abrechnung</th> : null}
                {zeigtKosten ? <th className="num">aufs Jahr</th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item.id} className="clickable" onClick={() => setDetail(item)}>
                  <td>
                    <div className="row-tight">
                      <ItemIcon item={item} size={22} />
                      <strong>{item.title}</strong>
                      {item.needsRotation ? <span className="badge badge-danger">ändern</span> : null}
                      {item.status === 'trial' ? <span className="badge badge-warn">Test</span> : null}
                      {item.criticality === 'high' ? <span className="badge">kritisch</span> : null}
                    </div>
                    <div className="small muted">
                      {[KIND_LABEL[item.kind], item.category, item.username].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className="small muted col-optional">
                    {item.kind === 'shared'
                      ? item.accessUids.length === 1
                        ? '1 Person'
                        : `${item.accessUids.length} Personen`
                      : `${item.assignments.filter((a) => a.openToAll || (a.assigneeUids ?? []).length > 0).length}${
                          item.seats ? ` von ${item.seats}` : ''
                        } ${item.kind === 'license' ? 'Lizenzen' : 'Plätze'}`}
                    {item.wrappedKey ? '' : ' · du verwaltest nicht'}
                  </td>
                  {zeigtKosten ? (
                    <td className="small muted col-optional">
                      {!item.hasCost
                        ? 'kostenlos'
                        : `${formatMoney(item.priceCents, item.currency)} ${CYCLE_LABEL[item.cycle]}${
                            item.priceIsPerSeat ? ' / Platz' : ''
                          }`}
                      {item.billingDay ? (
                        <div className="small muted">Abbuchung am {item.billingDay}.</div>
                      ) : null}
                    </td>
                  ) : null}
                  {zeigtKosten ? (
                    <td className="num mono">
                      {annualMoney(item).grossCents === 0
                        ? '–'
                        : formatMoney(annualMoney(item).grossCents)}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {liveDetail && !editing ? (
        <ItemDetail
          item={liveDetail}
          onEdit={() => setEditing({ item: liveDetail })}
          onClose={() => setDetail(null)}
        />
      ) : null}

      {editing ? (
        <ItemEditor
          item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            // Bei Plätzen und Lizenzen geht es nach dem Anlegen direkt weiter:
            // die Detailansicht ist der Ort, an dem sie vergeben werden.
            if (!editing.item && saved.kind !== 'shared') setDetail(saved);
          }}
        />
      ) : null}
    </>
  );
}
